import React, { useEffect, useState } from "react";
import { Column } from 'primereact/column';
import IApiStationResponse from "../../interfaces/IApiResponseGetAllStations";
import StationApi from "../../services/StationApi";
import StationDialog from "./dialog";
import { useToast } from "../../context/ToastContext";
import { useTranslation } from 'react-i18next';

// Design System
import PageHeader from "../../components/PageHeader/PageHeader";
import FilterPanel from "../../components/FilterPanel/FilterPanel";
import DataTable from "../../components/DataTable/DataTable";
import Input from "../../components/Input/Input";
import { Button } from "../../components/Button/Button";
import { IconButton } from "../../components/IconButton/IconButton";
import type { PaginatorPageChangeEvent } from "../../components/Paginator/Paginator";
import ConfirmModal from "../../components/ConfirmModal/ConfirmModal";


  const RegisterStation: React.FC = () => {

    const [stations, setStations] = useState<IApiStationResponse[]>([]);
    const [filteredEvents, setFilteredStations] = useState<IApiStationResponse[]>([]);

    const [inputValueEstacao, setInputValueEstacao] = useState<string | null>(null);
    const [inputValueDescricao, setInputValueDescricao] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [visible, setVisible] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [selectedStation, setSelectedStation] = useState<IApiStationResponse | null>(null);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [stationToDelete, setStationToDelete] = useState<string | null>(null);

    // Pagination/Sort state
    const [page, setPage] = useState(0);
    const [rows, setRows] = useState(10);
    const [sortField, setSortField] = useState<string>("name");
    const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");

    const { displayToast } = useToast();
    const { t: translateGeneral } = useTranslation("general");
    const { t: translateMessage } = useTranslation("messages");
    const { t: translateForm } = useTranslation("form");

    //#region  API

    const fetchStations = async (): Promise<void> => {
      setIsLoading(true);
      try {
        const response = await StationApi.GetAllStations();
        const fetchedStations = response.data || [];
        setStations(fetchedStations);
        setFilteredStations(fetchedStations);
      } catch (error) {
        console.error('Failed : ', error);
        displayToast(
            "error",
            "Não foi possivel carregar os dados.",
            "",
            false,
            3000
        );
      } finally {
        setIsLoading(false);
      }
    };
    

    useEffect(() => {
      fetchStations();
    }, []);  

    const createStation = async (name: string, description: string, sublineId: string): Promise<void> => {
      // Verifica se o nome e a descrição são iguais
      if (name.toLowerCase() === description.toLowerCase()) {
        displayToast("error", "O nome e a descrição não podem ser iguais!", "", false, 3000);
        return; // Impede a criação se nome e descrição forem iguais
      }
    
      // Verifica se já existe uma estação com o mesmo nome ou descrição
      const stationExists = stations.some(station => 
        station.name.toLowerCase() === name.toLowerCase() || 
        station.description.toLowerCase() === description.toLowerCase()
      );
    
      if (stationExists) {
        displayToast("error", "Já existe uma estação com esse nome ou descrição!", "", false, 3000);
        return; // Impede a criação se já existir
      }
    
      try {
        const response = await StationApi.PostStationCreate({ name, description, sublineId });
        if (response.data) {
          fetchStations(); // Recarrega a lista de estações após criação
          displayToast("success", "Estação criada com sucesso!", "", false, 3000);
        }
      } catch (error) {
        console.error('Failed: ', error);
        displayToast("error", "Erro ao cadastrar a estação.", "", false, 3000);
      }
    };
    

  const updateStation = async (id: string, name: string, description: string, sublineId: string): Promise<void> => {
      // Verifica se o nome e a descrição são iguais
      if (name.toLowerCase() === description.toLowerCase()) {
        displayToast("error", "O nome e a descrição não podem ser iguais!", "", false, 3000);
        return; // Impede a atualização se nome e descrição forem iguais
      }

      // Verifica se já existe outra estação com o mesmo nome ou descrição
      const stationExists = stations.some(station =>
        (station.name.toLowerCase() === name.toLowerCase() || 
        station.description.toLowerCase() === description.toLowerCase()) &&
        station.id !== id // Exclui a estação sendo editada
      );

      if (stationExists) {
        displayToast("error", "Já existe uma estação com esse nome ou descrição!", "", false, 3000);
        return; // Impede a edição se já existir outra estação com o mesmo nome ou descrição
      }

      try {
        const response = await StationApi.UpdateStation({ id, name, description, sublineId });
        if (response) {
          fetchStations(); // Atualiza a lista após editar
          displayToast("success", "Estação atualizada com sucesso!", "", false, 3000);
          setStations(prevStations => prevStations.map(station => 
            station.id === id ? { ...station, name, description, sublineId } : station
          ));
        } else {
          displayToast("error", "Erro ao atualizar a estação", "", false, 3000);
        }
      } catch (error) {
        console.error('Failed: ', error);
        displayToast("error", "Erro ao atualizar a estação.", "", false, 3000);
      }
  };

    
    



    const deleteStation = async (id: string): Promise<void> => {
      try {
          await StationApi.DeleteStation(id);
          fetchStations();
          displayToast(
              "success",
              "Estação excluída com sucesso!",
              "",
              false,
              3000
          );
      } catch (error) {
          console.error('Failed to delete station: ', error);
          displayToast(
              "error",
              "Erro ao excluir a estação.",
              "",
              false,
              3000
          );
      }
  };



  const handleEditClick = (station: IApiStationResponse): void => {
    setSelectedStation(station);
    setEditMode(true);
    setVisible(true);

  };




  const handleDialogSubmit = (name: string, description: string, sublineId: string): void => {
    if (editMode && selectedStation) {
      // Atualiza a estação
      updateStation(selectedStation.id, name, description, sublineId);
    } else {
      // Cria uma nova estação
      createStation(name, description, sublineId);
    }
    setVisible(false); // Fecha o diálogo
    setSelectedStation(null); // Limpa a estação selecionada
    setEditMode(false); // Limpa o modo de edição
  };





    //#endregion

    const resetFilters = (): void => {
        setInputValueEstacao(null);
        setInputValueDescricao(null);
        setFilteredStations(stations);
        setPage(0);
    };

    const filterStations = (): void => {
        let filtered = stations;

        if (inputValueEstacao) {
            filtered = filtered.filter((event: IApiStationResponse): boolean => {
                return event.name.toLowerCase().includes(inputValueEstacao.toLowerCase());
            });
        }

        if (inputValueDescricao) {
            filtered = filtered.filter((event: IApiStationResponse): boolean => {
                const p = event.subLine ? event.subLine.name?.toLowerCase() : "";
                return p.includes(inputValueDescricao.toLowerCase());
            });
        }

        setFilteredStations(filtered);
        setPage(0);
    };

    const handlePageChange = (e: PaginatorPageChangeEvent): void => {
        setPage(e.first);
        setRows(e.rows);
    };

    const handleSortChange = (e: any): void => {
        setSortField(e.sortField);
        setSortOrder(e.sortOrder === 1 ? "asc" : "desc");
    };

    const alertDelete = (stationId: string): void => {
      setStationToDelete(stationId);
      setShowDeleteConfirm(true);
    };
    //#endregion

    return (
        <div className="overflow-hidden">
            <PageHeader
                title={translateForm("stations")}
                description="Gerencie as estações de trabalho do sistema"
            >
                <Button
                    onClick={() => {
                        setSelectedStation(null);
                        setEditMode(false);
                        setVisible(true);
                    }}
                    label={translateGeneral("register")}
                    icon="add"
                    severity="primary"
                />
            </PageHeader>

            <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
                <FilterPanel
                    title="Filtros"
                    onApply={filterStations}
                    onClear={resetFilters}
                >
                    <Input
                        type="text"
                        label={translateForm("Station")}
                        value={inputValueEstacao || ""}
                        onChange={(val: any) => setInputValueEstacao(val)}
                    />
                    <Input
                        type="text"
                        label={translateForm("Subline")}
                        value={inputValueDescricao || ""}
                        onChange={(val: any) => setInputValueDescricao(val)}
                    />
                </FilterPanel>

                {isLoading ? (
                    <div>{translateGeneral("loading")}</div>
                ) : (
                <DataTable
                    value={filteredEvents}
                    paginated
                    first={page}
                    rows={rows}
                    totalRecords={filteredEvents.length}
                    onPageChange={handlePageChange}
                    rowsPerPageOptions={[10, 20, 50]}
                    loading={isLoading}
                    sortField={sortField}
                    sortOrder={sortOrder === "asc" ? 1 : -1}
                    onSort={handleSortChange}
                    emptyMessage={translateGeneral("noRecordsFound")}
                >
                    <Column
                        field="name"
                        header={translateForm("name")}
                        sortable
                    />
                    <Column
                        field="description"
                        header={translateForm("description")}
                        sortable
                    />
                    <Column
                        field="subLine.name"
                        header={translateForm("subline")}
                        sortable
                    />
                    <Column
                        field="actions"
                        header={translateGeneral("actions")}
                        body={(rowData: IApiStationResponse) => (
                            <div style={{ display: "flex", gap: "0.5rem" }}>
                                <IconButton
                                    icon="edit"
                                    variant="text"
                                    severity="secondary"
                                    onClick={() => handleEditClick(rowData)}
                                    aria-label={translateGeneral("edit")}
                                />
                                <IconButton
                                    icon="delete"
                                    variant="text"
                                    severity="secondary"
                                    onClick={() => alertDelete(rowData.id)}
                                    aria-label={translateGeneral("delete")}
                                />
                            </div>
                        )}
                        style={{ width: "8rem" }}
                    />
                </DataTable>
            )}

            <StationDialog
                visible={visible}
                onHide={() => setVisible(false)}
                onCreate={handleDialogSubmit}
                station={selectedStation ? { ...selectedStation, sublineId: selectedStation.subLineId ?? "" } : null}
            />

            <ConfirmModal
                visible={showDeleteConfirm}
                onHide={() => setShowDeleteConfirm(false)}
                onConfirm={() => {
                    if (stationToDelete) deleteStation(stationToDelete);
                    setShowDeleteConfirm(false);
                }}
                title={translateGeneral("confirmation")}
                message={translateMessage("deleteStation")}
            />
        </div>
    </div>
);
    //#endregion
  };

  export default RegisterStation;

